// Chart.js data + options built from a normalised ChartSpec. Pure: `import
// type` only, so this module can be exercised in the node-only test runner.
//
// The per-type policy lives here and nowhere else — docs/charts.md documents
// it, this file implements it: 2px lines with no dots at rest, bars with no
// borders and a thickness cap, arcs separated by a surface-coloured stroke,
// one legend per colour-encoded key, all text in ink/faint tokens (never a
// series colour), and every value axis starting at 0.

import type { ChartData, ChartDataset, ChartOptions } from 'chart.js'

import type { ChartTokens } from './chartTheme'
import { formatValue, xTickLabel, xTooltipLabel, type ChartSpec } from './chartSpec'

/** Series colours resolved in spec order, plus the shared chrome tokens. */
export type ChartColors = ChartTokens

const ARC = new Set(['pie', 'doughnut'])

function alpha(color: string, a: number): string {
  const hex = color.match(/^#([0-9a-f]{6})$/i)?.[1]
  if (hex) {
    const n = parseInt(hex, 16)
    return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`
  }
  const rgb = color.match(/^rgba?\(([^)]+)\)/)?.[1]
  if (rgb) {
    const [r, g, b] = rgb.split(',').map((s) => s.trim())
    return `rgba(${r}, ${g}, ${b}, ${a})`
  }
  return color
}

export function buildChartData(spec: ChartSpec, colors: ChartColors): ChartData {
  if (ARC.has(spec.type)) {
    const series = spec.series[0]
    return {
      labels: spec.labels,
      datasets: [{
        label: series?.label ?? '',
        data: (series?.data ?? []).map((v) => v ?? 0),
        backgroundColor: spec.labels.map((_, i) => colors.series[i % colors.series.length]),
        borderColor: colors.surface,
        borderWidth: 2,
      } as ChartDataset],
    }
  }

  const datasets = spec.series.map((s, i) => {
    const color = colors.series[i] ?? colors.series[colors.series.length - 1]
    if (spec.type === 'bar') {
      return {
        label: s.label,
        data: spec.categorical ? s.data : s.points.map((p) => p.y),
        backgroundColor: color,
        borderWidth: 0,
        borderRadius: 3,
        maxBarThickness: 28,
        categoryPercentage: 0.8,
        barPercentage: 0.9,
        yAxisID: s.axis,
      } as ChartDataset
    }
    return {
      label: s.label,
      data: spec.categorical ? s.data : s.points,
      borderColor: color,
      backgroundColor: s.fillAlpha ? alpha(color, s.fillAlpha) : 'transparent',
      fill: s.fillAlpha ? ('origin' as const) : false,
      borderWidth: s.muted ? 1.5 : 2,
      borderDash: s.dashed ? [5, 4] : undefined,
      pointRadius: spec.type === 'scatter' ? 3 : 0,
      pointHoverRadius: 5,
      pointHitRadius: 12,
      pointBackgroundColor: color,
      pointHoverBackgroundColor: color,
      pointHoverBorderColor: colors.surface,
      pointHoverBorderWidth: 2,
      showLine: spec.type !== 'scatter',
      tension: 0,
      yAxisID: s.axis,
    } as ChartDataset
  })

  return spec.categorical ? { labels: spec.labels.map((l) => l), datasets } : { datasets }
}

function valueAxis(spec: ChartSpec, colors: ChartColors, unit: string, right: boolean) {
  return {
    // Always from 0: a truncated value axis exaggerates every slope.
    min: 0,
    max: right ? undefined : spec.yMax ?? undefined,
    // No pinned max → leave head-room above the peak so the line doesn't run
    // along the top of its own frame. The right axis always scales to its own
    // series.
    suggestedMax: right
      ? spec.suggestedMaxRight ?? undefined
      : spec.yMax == null ? spec.suggestedMax ?? undefined : undefined,
    position: right ? ('right' as const) : ('left' as const),
    stacked: spec.stacked,
    grid: right
      ? { drawOnChartArea: false }
      : { color: alpha(colors.grid, 0.5), drawTicks: false },
    border: { display: false },
    title: spec.yLabel && !right ? { display: true, text: spec.yLabel, color: colors.faint } : undefined,
    ticks: {
      color: colors.faint,
      font: { size: 10 },
      maxTicksLimit: 4,
      callback: (v: string | number) => `${v}${unit}`,
    },
  }
}

function categoryAxis(spec: ChartSpec, colors: ChartColors) {
  return {
    type: 'category' as const,
    stacked: spec.stacked,
    grid: { display: false },
    border: { color: colors.grid },
    ticks: { color: colors.faint, font: { size: 10 }, maxRotation: 0, autoSkip: true },
  }
}

function continuousAxis(spec: ChartSpec, colors: ChartColors) {
  const xs = spec.series.flatMap((s) => s.points.map((p) => p.x))
  const min = spec.xMin ?? (xs.length ? Math.min(...xs) : 0)
  const max = spec.xMax ?? (xs.length ? Math.max(...xs) : 1)
  return {
    type: 'linear' as const,
    min,
    max,
    grid: { display: false },
    border: { color: colors.grid },
    title: spec.xLabel ? { display: true, text: spec.xLabel, color: colors.faint } : undefined,
    ticks: {
      color: colors.faint,
      font: { size: 10 },
      maxRotation: 0,
      // Chart.js picks its own linear ticks; we pin ours in afterBuildTicks.
      autoSkip: spec.type === 'scatter',
      maxTicksLimit: spec.type === 'scatter' ? 6 : undefined,
      callback: (v: string | number) => xTickLabel(spec, Number(v)),
    },
    afterBuildTicks: (axis: { ticks: { value: number }[] }) => {
      if (spec.type !== 'scatter' && spec.xTickValues.length) {
        axis.ticks = spec.xTickValues.map((value) => ({ value }))
      }
    },
  }
}

export function buildChartOptions(spec: ChartSpec, colors: ChartColors): ChartOptions {
  const arc = ARC.has(spec.type)
  // Slices are colour-encoded keys of their own, so a one-series pie still
  // needs a legend; line/bar/scatter key on the series.
  const showLegend = arc || spec.series.length > 1
  const perSeriesUnits = spec.units.length > 1

  const scales = arc
    ? undefined
    : spec.horizontal
      ? {
          x: valueAxis(spec, colors, spec.unit, false),
          y: categoryAxis(spec, colors),
        }
      : {
          x: spec.categorical ? categoryAxis(spec, colors) : continuousAxis(spec, colors),
          y: valueAxis(spec, colors, spec.units[0] ?? spec.unit, false),
          ...(perSeriesUnits ? { y1: valueAxis(spec, colors, spec.units[1], true) } : {}),
        }

  return {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    indexAxis: spec.horizontal ? ('y' as const) : ('x' as const),
    interaction: arc || spec.type === 'scatter'
      ? { mode: 'nearest' as const, intersect: spec.type === 'scatter' }
      : { mode: 'index' as const, intersect: false },
    ...(scales ? { scales } : {}),
    plugins: {
      legend: {
        display: showLegend,
        position: arc ? ('bottom' as const) : ('top' as const),
        labels: {
          color: colors.faint,
          boxWidth: 16,
          boxHeight: arc ? 8 : 0,
          font: { size: 10 },
          usePointStyle: arc,
        },
      },
      tooltip: {
        backgroundColor: alpha(colors.surface, 0.95),
        titleColor: colors.ink,
        bodyColor: colors.ink,
        borderColor: colors.grid,
        borderWidth: 1,
        padding: 8,
        displayColors: showLegend,
        callbacks: {
          title: (items: { parsed?: { x?: number }; label?: string }[]) => {
            if (!items.length) return ''
            if (spec.categorical || spec.horizontal) return items[0].label ?? ''
            const x = items[0].parsed?.x
            // The tooltip has room the axis doesn't: it names the day AND the
            // time, whichever half the tick had to drop.
            return x == null ? '' : xTooltipLabel(spec, x)
          },
          label: (item: { dataset?: { label?: string }; parsed?: { x?: number; y?: number } | number; datasetIndex?: number; label?: string }) => {
            const idx = item.datasetIndex ?? 0
            const unit = (perSeriesUnits ? spec.series[idx]?.unit : spec.unit) ?? ''
            const parsed = item.parsed
            const value = typeof parsed === 'number'
              ? parsed
              : spec.horizontal
                ? parsed?.x ?? 0
                : parsed?.y ?? 0
            const name = arc ? item.label : item.dataset?.label
            const prefix = showLegend && name ? `${name}: ` : ''
            return `${prefix}${formatValue(value, unit)}`
          },
        },
      },
    },
  } as ChartOptions
}
